import { Field, Item, Text, getFieldValue, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';
import classNames from 'classnames/bind';
import React, { ChangeEventHandler, useState } from 'react';
import type { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import styles from './BioListing.module.scss';
import { RecordOfFields } from 'models/fields';
import BioListingItem, { type BioItem } from './BioListingItem';
import { useI18n } from 'next-localization';
import Select, { type SelectOption } from 'components/helpers/Select/Select';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';

type BioListingProps = ComponentProps & {
  fields: {
    bioListingTitle: Field<string>;
    bioListingTitleHeadingLevel: Item | null;
    bioListingTitleHeadingTextStyle: Item | null;
    bioListingDescription: Field<string>;
    fallbackImage: string;
    geographicOptions: Item[];
    personItems?: BioItem[];
  };
};

const cx = classNames.bind(styles);

const BioListing = (props: BioListingProps): React.JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const { fields, className } = props;
  const showDropdown = props.params.showGeographicFiltering === '1';

  const personItems = fields?.personItems || [];
  const initialItems = showDropdown ? [] : personItems;

  // set up state for storing filtered bio items that will change based on county dropdown!
  const [filteredBios, setFilteredBios] = useState<BioItem[]>([...initialItems]);

  const outerContainer = React.useRef<null | HTMLDivElement>(null);

  const headingLevelItemField = fields?.bioListingTitleHeadingLevel?.fields as RecordOfFields;
  const titleHeadingLevelValue = getFieldValue<string>(headingLevelItemField, 'Value') || 'h2';

  const titleTextStyleItemField = fields?.bioListingTitleHeadingTextStyle?.fields as RecordOfFields;
  const titleTextStyleValue = getFieldValue<string>(titleTextStyleItemField, 'Value') || 'text-purple-60';

  const showTwoColumns = props.params.columns === '2';

  const options: SelectOption[] = [];

  const { t } = useI18n();

  props.fields?.geographicOptions?.forEach((geographicOption) => {
    if (!geographicOption.id) return;
    const valueField = geographicOption.fields.Value as Field<string>;
    const option: SelectOption = {
      id: geographicOption.id,
      value: `{${geographicOption.id?.toUpperCase()}}`,
      label: valueField,
    };
    options.push(option);
  });

  const updateBioItems: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const { value } = e.target;

    const newFilteredBios =
      value === ''
        ? personItems
        : [...personItems].filter((p) => {
            return p.supportedGeographicAreas.map((s) => s.id).includes(value);
          });

    setFilteredBios(newFilteredBios);
  };

  return (
    <div className={cx('bio-listing', 'component-container-edge', className)} ref={outerContainer}>
      <div className={cx('bio-listing-top')}>
        <div className={cx('bio-listing-title')}>
          <Text
            className={cx({ [titleTextStyleValue]: true }, 'mb-1')}
            encode={sitecoreContext.pageEditing}
            field={fields?.bioListingTitle}
            tag={titleHeadingLevelValue}
            editable={true}
          />
        </div>
        <div className={cx('bio-listing-content')}>
          <RichTextWrapper field={fields?.bioListingDescription} editable={true} />
        </div>
      </div>
      {showDropdown && props.fields?.geographicOptions?.length > 0 && (
        <div className={cx('bio-listing-select-options', 'mb-4')}>
          <Select
            id="geographicFilter"
            label={t('BioListingSelectACounty')}
            options={options}
            onChange={updateBioItems}
          />
        </div>
      )}
      <div className={cx('bio-listing-container', { 'two-col': showTwoColumns })}>
        {filteredBios.map((item, i) => {
          return (
            <BioListingItem
              key={`${item.id}-${i}`}
              item={item}
              params={props.params}
              fallbackImage={props.fields.fallbackImage}
            ></BioListingItem>
          );
        })}
      </div>
    </div>
  );
};

export default compose<BioListingProps>(withValidDatasourceCheck(), withStyles())(BioListing);
